<template>
  <b-card>
    <b-overlay
      :show="isLoadingMovie || isSaving"
      rounded="sm"
    >
      <!-- Topic header -->
      <div class="d-flex justify-content-between align-items-center mb-1">
        <h4 class="mb-0">
          {{ currentRt.autotopic_title || currentRt.topic_name || 'Тема' }}
        </h4>
        <b-badge variant="light-primary">
          {{ questionsCount }}/{{ questionsRequired }}
        </b-badge>
      </div>

      <!-- Existing questions -->
      <div
        v-if="existingQuestions.length"
        class="mb-2"
      >
        <div class="d-flex flex-wrap" style="gap: 6px">
          <b-badge
            v-for="q in existingQuestions"
            :key="q.id"
            variant="light-secondary"
            class="d-flex align-items-center"
            style="font-size: 0.85rem"
          >
            {{ q.price || '' }} — {{ q.answer || q.movie_title || '' }}
            <feather-icon
              icon="XIcon"
              size="14"
              class="ml-50 cursor-pointer"
              @click="$emit('question-deleted', q.id)"
            />
          </b-badge>
        </div>
      </div>

      <!-- Movie card -->
      <div v-if="suggestedMovie">
        <div class="d-flex align-items-start mb-1">
          <b-img
            v-if="suggestedMovie.poster"
            :src="suggestedMovie.poster"
            alt="Постер"
            width="80"
            class="mr-1 rounded"
          />
          <div>
            <h5 class="mb-25">
              {{ suggestedMovie.title_ru }}
            </h5>
            <p class="text-muted mb-25">
              {{ suggestedMovie.year }}
            </p>
            <small class="text-primary">Выбрано: {{ selectedIndices.length }}/3</small>
          </div>
        </div>

        <!-- Top action buttons -->
        <div class="d-flex mb-1" style="gap: 8px">
          <b-button
            variant="success"
            :disabled="selectedIndices.length !== 3 || isSaving"
            @click="onAccept"
          >
            Принять вопрос
          </b-button>
          <b-button
            variant="outline-secondary"
            :disabled="isLoadingMovie"
            @click="$emit('skipped')"
          >
            Другой фильм
          </b-button>
        </div>

        <!-- Frames grid -->
        <div class="frames-grid mb-1">
          <div
            v-for="(frame, idx) in suggestedMovie.frames"
            :key="frame.id || idx"
            class="frame-item-wrapper"
          >
            <div
              class="frame-item"
              :class="{
                'frame-selected': isSelected(idx),
                'frame-disabled': !isSelected(idx) && selectedIndices.length >= 3,
              }"
              @click="toggleFrame(idx)"
            >
              <img
                :src="frame.preview_url || frame.url"
                :alt="'Кадр ' + (frame.id || idx + 1)"
                class="frame-img"
              >
              <div
                v-if="isSelected(idx)"
                class="frame-badge"
              >
                {{ getFrameOrder(idx) }}
              </div>
              <div
                class="frame-preview-btn"
                @click.stop="openPreview(frame)"
              >
                <feather-icon
                  icon="MaximizeIcon"
                  size="14"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Preview modal -->
        <b-modal
          v-model="showPreview"
          centered
          hide-footer
          size="xl"
          body-class="p-0"
          header-class="pb-0 border-0"
        >
          <img
            v-if="previewFrame"
            :src="previewFrame.url"
            alt="Превью кадра"
            style="max-width: 100%; max-height: calc(100vh - 120px); object-fit: contain; display: block; margin: 0 auto"
          >
        </b-modal>

        <!-- Action buttons -->
        <div class="d-flex" style="gap: 8px">
          <b-button
            variant="success"
            :disabled="selectedIndices.length !== 3 || isSaving"
            @click="onAccept"
          >
            Принять вопрос
          </b-button>
          <b-button
            variant="outline-secondary"
            :disabled="isLoadingMovie"
            @click="$emit('skipped')"
          >
            Другой фильм
          </b-button>
        </div>

        <hr>

        <b-button
          variant="outline-danger"
          size="sm"
          @click="$emit('topic-removed')"
        >
          Убрать тему
        </b-button>
      </div>

      <div v-else-if="!isLoadingMovie">
        <p class="text-muted my-2">
          Нет фильмов с кадрами для этой темы
        </p>
        <b-button
          variant="outline-danger"
          size="sm"
          @click="$emit('topic-removed')"
        >
          Убрать тему
        </b-button>
      </div>
    </b-overlay>
  </b-card>
</template>

<script>
import {
  BCard, BButton, BOverlay, BBadge, BImg, BModal,
} from 'bootstrap-vue'
import { ref, watch, computed } from '@vue/composition-api'

export default {
  components: {
    BCard,
    BButton,
    BOverlay,
    BBadge,
    BImg,
    BModal,
  },
  props: {
    gameId: {
      type: [Number, String],
      required: true,
    },
    rtId: {
      type: [Number, String],
      required: true,
    },
    currentRt: {
      type: Object,
      required: true,
    },
    suggestedMovie: {
      type: Object,
      default: null,
    },
    isLoadingMovie: {
      type: Boolean,
      default: false,
    },
    framesSource: {
      type: String,
      default: null,
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const selectedIndices = ref([])

    const existingQuestions = computed(() => props.currentRt.questions || [])

    const questionsRequired = computed(() => props.currentRt.required_questions || 5)

    const questionsCount = computed(() => existingQuestions.value.length)

    watch(() => props.suggestedMovie, () => {
      selectedIndices.value = []
    })

    const isSelected = idx => selectedIndices.value.includes(idx)

    const getFrameOrder = idx => selectedIndices.value.indexOf(idx) + 1

    const toggleFrame = idx => {
      const pos = selectedIndices.value.indexOf(idx)
      if (pos !== -1) {
        selectedIndices.value.splice(pos, 1)
      } else if (selectedIndices.value.length < 3) {
        selectedIndices.value.push(idx)
      }
    }

    const showPreview = ref(false)
    const previewFrame = ref(null)

    const openPreview = frame => {
      previewFrame.value = frame
      showPreview.value = true
    }

    const onAccept = () => {
      const selected = selectedIndices.value.map(i => props.suggestedMovie.frames[i])
      if (props.framesSource === 'api') {
        emit('accepted', {
          movie_id: props.suggestedMovie.id,
          frames: selected.map(f => ({ url: f.url, preview_url: f.preview_url })),
        })
      } else {
        emit('accepted', {
          movie_id: props.suggestedMovie.id,
          frame_ids: selected.map(f => f.id),
        })
      }
    }

    return {
      selectedIndices,
      existingQuestions,
      questionsRequired,
      questionsCount,
      isSelected,
      getFrameOrder,
      toggleFrame,
      showPreview,
      previewFrame,
      openPreview,
      onAccept,
    }
  },
}
</script>

<style scoped>
.frames-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 0.75rem;
  max-height: calc(100vh - 320px);
  overflow-y: auto;
  padding-right: 4px;
}

.frame-item-wrapper {
  position: relative;
  aspect-ratio: 16 / 9;
}

.frame-item {
  width: 100%;
  height: 100%;
  border-radius: 0.5rem;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid #ebe9f1;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f8f8;
  position: relative;
  transition: border-color 0.15s, opacity 0.15s;
}

.frame-item .frame-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.frame-item.frame-selected {
  border-color: #7367f0;
  border-width: 3px;
}

.frame-item.frame-disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.frame-badge {
  position: absolute;
  top: 6px;
  left: 6px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #7367f0;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
}

.frame-preview-btn {
  position: absolute;
  bottom: 6px;
  right: 6px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.55);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s;
}

.frame-item:hover .frame-preview-btn {
  opacity: 1;
}
</style>
